import { useRef, useState } from "react";

import { useClickOutside } from "@/lib/hooks/useClickOutside";

/** Dropdown with checkboxes (U9) — multi-select styled to be indistinguishable
 *  from `select.search` (same CSS-drawn chevron). Tick any combination;
 *  nothing ticked = "all" (no filter). */
export function MultiSelect<V extends string | number>({
  options, value, onChange, emptyLabel = "All", noneLabel = "none available", clearLabel = "Clear - all",
  disabled = false,
}: {
  options: { value: V; label: string }[];
  value: V[];
  onChange: (v: V[]) => void;
  emptyLabel?: string;
  noneLabel?: string;
  clearLabel?: string;
  /** Read-only: still shows WHAT IS SELECTED, but cannot be opened. A summary
   *  the reader can see beats hiding the control, which reads as "not set". */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const summary = value.length
    ? options.filter((o) => value.includes(o.value)).map((o) => o.label).join(", ")
    : emptyLabel;

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <button type="button" className="search" disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", minWidth: 0, marginLeft: 0, textAlign: "left",
          cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1,
          color: "var(--t-bright)", backgroundColor: "var(--surface)", paddingRight: 32,
          backgroundImage: "linear-gradient(45deg,transparent 50%,var(--t-mute) 50%), linear-gradient(135deg,var(--t-mute) 50%,transparent 50%)",
          backgroundPosition: "calc(100% - 16px) calc(50% - 2px), calc(100% - 11px) calc(50% - 2px)",
          backgroundSize: "5px 5px, 5px 5px", backgroundRepeat: "no-repeat",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {summary}
      </button>
      {open && !disabled && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 40,
          background: "var(--surface)", border: "1px solid var(--line2)", borderRadius: 10,
          boxShadow: "var(--shadow)", padding: 6, maxHeight: 220, overflowY: "auto" }}>
          {options.length === 0 && <div style={{ padding: 8, fontSize: 11.5, color: "var(--t-dim)" }}>{noneLabel}</div>}
          {options.map((o) => (
            <label key={String(o.value)} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px",
              borderRadius: 6, cursor: "pointer", fontSize: 12, color: "var(--t-body)" }}>
              <input type="checkbox" checked={value.includes(o.value)}
                onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((x) => x !== o.value))} />
              {o.label}
            </label>
          ))}
          {value.length > 0 && (
            <button type="button" className="btn ghost" style={{ margin: "4px 6px 2px", width: "calc(100% - 12px)" }}
              onClick={() => onChange([])}>{clearLabel}</button>
          )}
        </div>
      )}
    </div>
  );
}
